import { useCallback, useEffect, useRef, useState } from 'react';
import { api, FolderWithMeta, OrgProject } from '../api';
import { getErrorMessage } from '../../../shared/src';

interface MoveFolderModalProps {
  orgId: string;
  /** The folder being moved (source project is derived from it server-side). */
  folder: FolderWithMeta;
  /** The project currently being browsed — excluded from the destination list. */
  currentProjectId: string;
  availableProjects: OrgProject[];
  /**
   * Closes the modal. When the operator closes while files are still being
   * relocated, `incomplete` carries the counts so the parent can leave a durable
   * notice (the move committed, but some files weren't relocated yet).
   */
  onClose: (incomplete?: { relocated: number; total: number }) => void;
  /** Fired once the move has committed, so the parent can refresh its listing. */
  onMoved: () => void;
}

type Phase = 'confirm' | 'relocating' | 'done';

/**
 * Move a folder (with its whole subtree) into another project in the same org.
 * The folder lands at the destination project's root.
 *
 * The move itself (the atomic DB flip) is one fast request; the S3 re-key that
 * follows can be long for big folders, so it's driven here as a loop of bounded
 * `rekey-batch` calls with a live progress bar. The destination inherits its own
 * project's permissions (source members lose access, target members gain it) —
 * the warning makes that explicit before the operator confirms.
 */
export function MoveFolderModal({
  orgId,
  folder,
  currentProjectId,
  availableProjects,
  onClose,
  onMoved,
}: MoveFolderModalProps) {
  const destinations = availableProjects.filter((p) => p.id !== currentProjectId);
  const [targetProjectId, setTargetProjectId] = useState('');
  const [phase, setPhase] = useState<Phase>('confirm');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ relocated: 0, total: 0 });
  const [busy, setBusy] = useState(false);

  // Stops the re-key loop if the modal is closed/unmounted mid-flight. Reset to
  // false on mount as well: React StrictMode double-invokes effects
  // (setup → cleanup → setup) in dev, so a cleanup-only effect would leave this
  // stuck `true` after mount and the loop would never run.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  // Guards against a second concurrent loop (e.g. a fast double-click on Retry).
  const runningRef = useRef(false);

  // Drive the progressive re-key: loop bounded batches until the server says done,
  // accumulating each batch's `moved` into the progress bar. A zero-progress batch
  // returns an error from the server, which surfaces here as a retryable error.
  const runRekeyLoop = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setBusy(true);
    setError(null);
    try {
      for (;;) {
        if (cancelledRef.current) return;
        const res = await api.rekeyFolderBatch(orgId, folder.id);
        if (cancelledRef.current) return;
        setProgress((p) => ({ total: p.total, relocated: Math.min(p.total, p.relocated + res.moved) }));
        if (res.done) {
          setPhase('done');
          return;
        }
      }
    } catch (err: unknown) {
      if (!cancelledRef.current) setError(getErrorMessage(err));
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  }, [orgId, folder.id]);

  const handleMove = async () => {
    if (!targetProjectId) {
      setError('Choose a destination project.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.moveOrgFolder(orgId, folder.id, targetProjectId);
      // The move committed — let the parent refresh its listing immediately.
      onMoved();
      setProgress({ relocated: result.relocated, total: result.total });
      if (result.total === 0 || result.relocated >= result.total) {
        setPhase('done');
      } else {
        setPhase('relocating');
        void runRekeyLoop();
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return; // don't interrupt the initial move commit
    cancelledRef.current = true;
    // Closing while files are still being relocated → tell the parent so it can
    // leave a durable notice (the move committed, but relocation isn't finished).
    const incomplete = phase === 'relocating' && progress.relocated < progress.total;
    onClose(incomplete ? { relocated: progress.relocated, total: progress.total } : undefined);
  };

  const pct = progress.total > 0 ? Math.round((progress.relocated / progress.total) * 100) : 100;
  const remaining = Math.max(0, progress.total - progress.relocated);

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Move "{folder.name}" to another project</h2>

        {phase === 'confirm' && (
          destinations.length === 0 ? (
            <>
              <p className="modal-hint">
                There are no other projects in this organisation to move this folder into.
              </p>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleClose}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="modal-hint">
                The folder and everything inside it (subfolders and files) moves to the
                destination project's top level.
              </p>
              <div className="modal-warning">
                <strong>Access changes.</strong> Files inherit the destination project's
                permissions. Members of the current project will lose access to this folder,
                and members of the destination project will gain it.
              </div>
              <label className="modal-field">
                <span>Destination project</span>
                <select
                  value={targetProjectId}
                  onChange={(e) => setTargetProjectId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select a project…</option>
                  {destinations.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.slug})
                    </option>
                  ))}
                </select>
              </label>
              {error && <div className="modal-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleClose} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleMove}
                  disabled={saving || !targetProjectId}
                >
                  {saving ? 'Moving…' : 'Move'}
                </button>
              </div>
            </>
          )
        )}

        {phase === 'relocating' && (
          <>
            <p className="modal-hint">
              Folder moved. Relocating its files in storage — you can leave this window open.
            </p>
            <div className="move-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="move-progress-track">
                <div className="move-progress-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="move-progress-label">
                {progress.relocated.toLocaleString()} / {progress.total.toLocaleString()} files ({pct}%)
              </div>
            </div>
            {error && (
              <>
                <div className="modal-error">{error}</div>
                <p className="modal-hint">
                  The move itself is done. You can retry relocating the remaining files, or close —
                  they'll be relocated automatically on a later move.
                </p>
              </>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={handleClose}>
                Close
              </button>
              {error && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => void runRekeyLoop()}
                  disabled={busy}
                >
                  {busy ? 'Relocating…' : 'Retry'}
                </button>
              )}
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            {remaining === 0 ? (
              <p className="modal-hint">
                ✓ Move complete. {progress.total.toLocaleString()} file{progress.total === 1 ? '' : 's'} relocated.
              </p>
            ) : (
              <p className="modal-hint">
                Move complete. {progress.relocated.toLocaleString()} of {progress.total.toLocaleString()} files
                relocated; the remaining {remaining.toLocaleString()} will be relocated automatically on a later move.
              </p>
            )}
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
